/**
 * The rules behind the Audit Log — pure, so they can be tested without a browser
 * or SharePoint. Nothing here imports the SharePoint client (which needs tenant
 * config at import), for the same reason presenceWindow.ts does not.
 *
 * ── WHAT A SESSION IS ──────────────────────────────────────────────────────
 * Time somebody had i-SYNERGY open, VISIBLE, and had touched it within the last
 * five minutes. Not merely "a tab is open": a Production Grid left on a wall
 * display would otherwise log twenty-four-hour sessions and the column would
 * mean nothing. A keyboard-wedge barcode scanner counts as touching it.
 *
 * ── WHERE THE TIMES COME FROM ──────────────────────────────────────────────
 * START is the row's `Created` and END its `Modified`, both stamped by
 * SharePoint — never a laptop clock. Presence wrote the writer's clock and was
 * read against the reader's, and two laptops a few minutes apart disagreed
 * about who was online (2026-10-01). A session's length is the difference of
 * two stamps from the SAME server clock, so no laptop can bend it.
 *
 * The laptop's clock is still used for two decisions that never leave that
 * laptop — "has this person been idle?" and "is the stored session recent
 * enough to carry on?" — and those compare the laptop with ITSELF, so a wrong
 * clock cancels out.
 *
 * ── HOW PRECISE ────────────────────────────────────────────────────────────
 * Resolution is the heartbeat (45 s). END can run up to the idle limit (5 min)
 * past the last real touch, because the app keeps beating until it decides
 * somebody has gone. It is "about how long", not a stopwatch.
 */

/** How often an active session writes its heartbeat. */
export const AUDIT_BEAT_MS = 45_000;
/** No touch for this long and the session stops counting. */
export const AUDIT_IDLE_MS = 5 * 60_000;
/**
 * A returning person carries on the SAME session if their last heartbeat is
 * newer than this; older and it is a new one. It is deliberately a little
 * longer than a couple of missed beats, so a throttled background tab does not
 * split one sitting into several rows.
 */
export const AUDIT_SESSION_GAP_MS = 3 * 60_000;

/** The session this browser is already writing to, kept in localStorage. */
export interface IStoredSession {
  email: string;
  rowId: number;
  /** This laptop's own clock at the last heartbeat — compared only with itself. */
  lastBeatMs: number;
  beats: number;
}

export type BeatDecision = "skip" | "create" | "update";

export function decideBeat(o: {
  nowMs: number;
  lastActivityMs: number;
  visible: boolean;
  email: string;
  stored: IStoredSession | null;
}): BeatDecision {
  if (!o.visible) return "skip";
  if (o.nowMs - o.lastActivityMs > AUDIT_IDLE_MS) return "skip";
  const s = o.stored;
  // Somebody else's session (a shared machine signed into a new account), or
  // one that has been quiet too long, is not ours to continue.
  if (!s || s.email !== o.email || o.nowMs - s.lastBeatMs > AUDIT_SESSION_GAP_MS) return "create";
  return "update";
}

// ─── Rows ────────────────────────────────────────────────────────────────────

/** A row of iSyn_AuditLog as SharePoint returns it. */
export interface IAuditRaw {
  Id: number;
  /** The account's sign-in address. */
  Title?: string | null;
  DisplayName?: string | null;
  Created?: string | null;
  Modified?: string | null;
}

export interface IAuditRow {
  id: number;
  email: string;
  name: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  /** Worked out from other lists' scans and slips, not recorded as it happened. */
  estimated?: boolean;
}

/**
 * Turn raw rows into what the screen shows, newest first.
 *
 * `nameFor` is `displayNameFor` in the app: most people sign in with a ROLE
 * mailbox, so the stored name is often "operations" while the person is Paolo
 * Obrero. It is resolved here, when read, so adding someone to the directory
 * fixes every past row as well as the next one.
 *
 * A row with no account or no readable start is dropped — a log line that
 * cannot say who or when is noise, not evidence. END never precedes START
 * (`Modified` is a server stamp, but a list edited by hand could break that).
 */
export function toAuditRows(
  raw: readonly IAuditRaw[],
  nameFor: (email: string, fallback: string) => string,
): IAuditRow[] {
  const out: IAuditRow[] = [];
  for (const r of raw) {
    const email = (r.Title ?? "").trim().toLowerCase();
    const startMs = r.Created ? Date.parse(r.Created) : NaN;
    if (!email || !Number.isFinite(startMs)) continue;
    const modified = r.Modified ? Date.parse(r.Modified) : NaN;
    const endMs = Number.isFinite(modified) ? Math.max(startMs, modified) : startMs;
    out.push({
      id: r.Id,
      email,
      name: nameFor(email, (r.DisplayName ?? "").trim() || email),
      startMs,
      endMs,
      durationMs: endMs - startMs,
    });
  }
  return out.sort((a, b) => b.startMs - a.startMs || b.id - a.id);
}

export interface IPersonTotal {
  email: string;
  name: string;
  sessions: number;
  totalMs: number;
  lastSeenMs: number;
}

/** Time per person over whatever rows are given, longest first. */
export function totalsByPerson(rows: readonly IAuditRow[]): IPersonTotal[] {
  const by = new Map<string, IPersonTotal>();
  for (const r of rows) {
    const t = by.get(r.email) ?? { email: r.email, name: r.name, sessions: 0, totalMs: 0, lastSeenMs: 0 };
    t.sessions += 1;
    t.totalMs += r.durationMs;
    t.lastSeenMs = Math.max(t.lastSeenMs, r.endMs);
    by.set(r.email, t);
  }
  return [...by.values()].sort((a, b) => b.totalMs - a.totalMs || a.name.localeCompare(b.name));
}

// ─── Formatting ──────────────────────────────────────────────────────────────

/**
 * "42 min", "1 h 25 min", "2 h", "< 1 min". Floors, so a stretch is never
 * rounded up into a minute that did not happen; a row of 59 s reads "< 1 min"
 * rather than 0.
 */
export function formatDuration(ms: number): string {
  if (!(ms >= 60_000)) return "< 1 min";
  const total = Math.floor(ms / 60_000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** mm/dd/yyyy in the viewer's own timezone — the app's date style. */
export function formatDate(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
}

/**
 * "2:05 PM". Built by hand, not with toLocaleTimeString: newer ICU puts a
 * narrow no-break space before AM/PM, which sits invisibly in a cell and breaks
 * any search or paste for the plain string.
 */
export function formatTime(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  return `${h % 12 || 12}:${pad2(d.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
}

/** Whole calendar days between two instants, in the viewer's timezone. */
function dayDiff(startMs: number, endMs: number): number {
  const a = new Date(startMs);
  const b = new Date(endMs);
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / 86_400_000);
}

/** The end time, saying so when it falls on a later day than the start. */
export function formatEnd(startMs: number, endMs: number): string {
  const days = dayDiff(startMs, endMs);
  const t = formatTime(endMs);
  if (days <= 0) return t;
  return `${t} (+${days} ${days === 1 ? "day" : "days"})`;
}

// ─── Date range ──────────────────────────────────────────────────────────────

/** yyyy-mm-dd in the viewer's timezone — the value a date input holds. */
export function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** The last seven days including today. */
export function defaultRange(now: Date): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
  return { from: ymd(from), to: ymd(now) };
}

function parseYmd(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // new Date rolls 2026-02-31 over to March; reject what it had to correct.
  return ymd(d) === s ? d : null;
}

/**
 * The instants a date range covers: from the START of the first day to the
 * START of the day after the last, so "to" is inclusive of the whole day. Day
 * boundaries are the viewer's, because "today" means their today. A reversed
 * pair is put the right way round rather than refused; an unparseable one is.
 */
export function dayRangeIso(from: string, to: string): { fromIso: string; toIso: string } | null {
  let a = parseYmd(from);
  let b = parseYmd(to);
  if (!a || !b) return null;
  if (a.getTime() > b.getTime()) [a, b] = [b, a];
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate() + 1);
  return { fromIso: a.toISOString(), toIso: end.toISOString() };
}

/**
 * Is this SharePoint error "the list is not there yet"? The list is created the
 * first time anyone's session is recorded, so before that the log is simply
 * empty rather than broken.
 *
 * Narrow on purpose. A missing COLUMN says "The field or property 'X' does not
 * exist", and matching on "does not exist" alone would read that as an empty
 * log and hide a half-provisioned list for ever.
 */
export function isMissingListError(message: string): boolean {
  return /\b404\b/.test(message) || /List '[^']*' does not exist/i.test(message);
}
